import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { PopoverOptionsComponent } from "src/components/popup/popover-options/popover-options.component";
import { AccountUpdate } from "src/model/class/general classes/account";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { UsersRole } from "src/model/enum/enumDigitalPlace";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iCredential } from "src/model/interface/icredential";
import { AccountService } from "src/service/account.service";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { SecuretyDataProtectionService } from "src/service/securety-data-protection.service";
import { SystemRouterService } from "src/service/system-router.service";
import { currentUserUpdatePassword } from "src/state/actions/authentication.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { PasswordValidators } from "src/validators/password-validators";

@Component({
    selector: "app-form-user-change-password",
    templateUrl: "./form-user-change-password.component.html",
    styleUrls: ["./form-user-change-password.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormUserChangePasswordComponent implements OnInit {
  private _logContext: string = "form-user-change-password.component";
  private _formSubscriptions: Array<Subscription> = null;
  private _translateSubscription: Subscription = null;
  private _errorUnAuthorizeSubscription: Subscription = null;
  private _keyboardSubscriptions: Subscription = null;
  private _isPerformingLogin: boolean = false;
  private _errorCredentialLoginCount: number = 0;
  private _onPerformUnauthorizeAccess = new Subject<number>();
  public arrayRole: Array<UsersRole> = new Array<UsersRole>();

  //#region Translate variable

  private _lblpasswordMustMatch: string = "";
  private _lblError: string = "";
  private _lblErrorCurrentPassword = "";
  private _selectCurrentUserSub: Subscription = null;
  private _selectedUser: CurrentSessionData = null;
  //#endregion

  public formCreateAccount: UntypedFormGroup = null;
  public cssPlatformClass: string = "";
  public forcedActive: boolean = false;
  @Output() onSavePassword: EventEmitter<void> = new EventEmitter();

  constructor(
    private _fb: UntypedFormBuilder,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _routerService: SystemRouterService,
    private _systemRouterService: SystemRouterService,
    private _accountService: AccountService,
    private _loaderService: LoaderService,
    public popoverController: PopoverController,
    private _store: Store,
    private _securetyDataProtectionService: SecuretyDataProtectionService
  ) { }

  ngOnInit() {
    this.forcedActive = false;

    this.loadTranslation();
    //this.subscribeTranslationEvent();

    this.initializeCreateAccountForm();
    this.subscribeFormEvent();
  }

  ngOnDestroy() {
    this.unsubscribeFormEvent();

    this._translateSubscription?.unsubscribe();
    this._errorUnAuthorizeSubscription?.unsubscribe();
    this._keyboardSubscriptions?.unsubscribe();
  }

  //Show tooltip with requirements for password
  public async showPopover() {
    const popover = await this.popoverController.create({
      component: PopoverOptionsComponent,
      event: event,
      translucent: false,
      showBackdrop: false,
    });

    await popover.present();
  }

  /**
   * Load translated strings
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.errorCurrentPassword", "dictionary.error"])
      .subscribe((translatedStrings) => {
        this._lblError = translatedStrings["dictionary.error"];
        this._lblErrorCurrentPassword =
          translatedStrings["dictionary.errorCurrentPassword"];
      });
  }

  //#endregion

  /**
   * Handle callback event for request login
   *
   * @param objectLogin
   */
  public async onRequestLogin(objectLogin: iCredential): Promise<void> {
    if (!this._isPerformingLogin) {
    }
  }

  /**
   * Enter pressed in a field: same as the confirm button, when the form is valid
   */
  public async onSubmitForm(): Promise<void> {
    if (this.formCreateAccount.valid) {
      await this.onClickConfirm();
    }
  }

  /**
   * Perform create account procedure.
   *
   * @returns
   */
  public async onClickConfirm(): Promise<void> {
    try {
      if (
        this.formCreateAccount.value.confirmPassword ==
        this.formCreateAccount.value.password
      ) {
        let accountNew: AccountUpdate = this.formCreateAccount.value;
        accountNew.password = this.formCreateAccount.get("password").value;

        let encryptedPassword = this._securetyDataProtectionService.encrypt(
          this.formCreateAccount.get("oldPassword").value
        );
        let encryptedNewPassword = this._securetyDataProtectionService.encrypt(
          this.formCreateAccount.get("password").value
        );
        if (this._selectedUser.password == encryptedPassword) {
          await this._accountService.performDPEditPassword(accountNew);
          this._store.dispatch(
            currentUserUpdatePassword({
              password: encryptedNewPassword,
            })
          );

          this.onSavePassword.emit();
        } else {
          this._alertService.presentAlertOk(
            "",
            this._lblError,
            this._lblErrorCurrentPassword
          );
        }
      } else {
        this.formCreateAccount
          .get("confirmPassword")
          .setErrors({ incorrect: true });
      }
    } catch (exception) { }
  }

  /**
   * Initialize login form plant
   */
  private initializeCreateAccountForm(): void {
    this.formCreateAccount = this._fb.group({
      oldPassword: new UntypedFormControl("", {
        validators: Validators.compose([
          Validators.minLength(4),
          Validators.required,
          PasswordValidators.isPWDCorret(),
        ]),
      }),
      password: new UntypedFormControl("", {
        validators: Validators.compose([
          Validators.minLength(4),
          Validators.required,
          PasswordValidators.isPWDCorret(),
        ]),
      }),
      confirmPassword: new UntypedFormControl("", {
        validators: Validators.compose([
          Validators.minLength(4),
          Validators.required,
          PasswordValidators.isPWDCorret(),
        ]),
      }),
    });
  }

  /**
   * Subscribe value change event from each form field.
   */
  private subscribeFormEvent(): void {
    this._formSubscriptions = new Array<Subscription>();
    // "The two passwords must match" under the confirmation field, while typing
    this._formSubscriptions.push(PasswordValidators.watchPasswordMatch(this.formCreateAccount));
    this.subscribeToCurrentUser();
  }

  /**
   * Subscribe to current user
   */
  private subscribeToCurrentUser() {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((user: CurrentSessionData) => {
        this._selectedUser = user;
      });
  }

  /**
   * Unsubscribe to form event events
   */
  private unsubscribeFormEvent(): void {
    for (const sub of this._formSubscriptions) {
      sub.unsubscribe();
    }
  }
}
